import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { animate, motion, useReducedMotion } from 'framer-motion';

import type { SanityStats } from '#/lib/sanity/queries/stats';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';

import { formatHeadline } from './Headline';

// easeOutCubic — a gentler curve than out-quint so the movement stays
// perceptible across the full duration instead of snapping into place.
const EASE = [0.33, 1, 0.68, 1] as const;

const CARD_STAGGER = 0.15;

// Staggers each stat card in sequence.
const gridVariants = {
  hidden: {},
  show: { transition: { staggerChildren: CARD_STAGGER } },
};

const ENTRANCE_DURATION = 0.7;

const itemVariants = {
  hidden: { opacity: 0, y: 48 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: ENTRANCE_DURATION, ease: EASE },
  },
};

const MOTION_PROPS = {
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, margin: '-100px' },
} as const;

// NOTE: this module has its own theme vocabulary. Dark variants must stay
// registered in MODULE_DARK_THEME_OVERRIDES (lib/section-theme.ts) so the
// nav inverts over them.
const THEME_STYLES = {
  charcoal: {
    section: 'bg-charcoal text-cloud',
    card: 'bg-grayscale-06 text-electric',
    accent: 'text-electric',
  },
  electric: {
    section: 'bg-charcoal text-cloud',
    card: 'bg-electric text-charcoal',
    accent: 'text-electric',
  },
  purple: {
    section: 'bg-cipher-05 text-cloud',
    card: 'bg-cipher-600 text-cloud',
    accent: 'text-electric',
  },
  whiteCipher: {
    section: 'bg-white text-charcoal',
    card: 'bg-cipher-01 text-cipher',
    accent: 'text-cipher',
  },
} as const;

const COUNT_DURATION = 1.4;

// How long before the card entrance finishes that the count-up kicks off,
// so the two animations overlap slightly instead of feeling sequential.
const COUNT_OVERLAP = 0.2;

// Splits a stat value into prefix / digits / suffix, e.g.
// "$1.9B" -> ["$", "1.9", "B"], "411M" -> ["", "411", "M"].
const VALUE_PATTERN = /^([^\d]*)([\d,]+(?:\.\d+)?)(.*)$/;

function formatCount(value: number, decimals: number, useGrouping: boolean) {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  });
}

function parseValue(value: string) {
  const match = value.match(VALUE_PATTERN);
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const decimals = digits.split('.')[1]?.length ?? 0;
  const useGrouping = digits.includes(',');
  const target = Number.parseFloat(digits.replace(/,/g, ''));

  // Values up to 60 count from 0; larger values count only the last 60
  // units (e.g. 411 counts 351 -> 411) so every stat travels a similar
  // distance over the shared duration.
  const from = Math.max(target - 60, 0);

  return { prefix, digits, suffix, decimals, useGrouping, target, from };
}

function AnimatedStatValue({
  value,
  start,
}: {
  value: string;
  start: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const node = ref.current;
    const parsed = parseValue(value);
    if (prefersReducedMotion || !parsed || !node) return;

    // While the card entrance is playing, hold the start value so the
    // count doesn't visibly jump backwards when it begins.
    if (!start) {
      node.textContent = formatCount(
        parsed.from,
        parsed.decimals,
        parsed.useGrouping,
      );
      return;
    }

    const controls = animate(parsed.from, parsed.target, {
      duration: COUNT_DURATION,
      ease: EASE,
      onUpdate: (latest) => {
        node.textContent = formatCount(
          latest,
          parsed.decimals,
          parsed.useGrouping,
        );
      },
    });
    return () => controls.stop();
  }, [start, prefersReducedMotion, value]);

  const parsed = parseValue(value);
  // Values without digits can't count; render them unchanged.
  if (!parsed) return <>{value}</>;

  // The server (and no-JS / reduced-motion clients) renders the final value.
  return (
    <>
      {parsed.prefix}
      <span ref={ref} className="tabular-nums">
        {parsed.digits}
      </span>
      {parsed.suffix}
    </>
  );
}

function StatCard(props: {
  stat: SanityStats['stats'][number];
  index: number;
  animateValue: boolean;
  valueClass: string;
  cardClass?: string;
  cardStyle?: CSSProperties;
  sanity?: string;
}) {
  // The count-up starts just before this card's entrance animation finishes.
  const [entered, setEntered] = useState(false);
  const startTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => () => clearTimeout(startTimeout.current), []);

  return (
    <motion.div
      variants={itemVariants}
      // AnimationStart fires for every staggered child at the moment the
      // grid is triggered (the stagger delay is applied inside the
      // animation), so each card's stagger offset is added here manually.
      onAnimationStart={(definition) => {
        if (definition !== 'show' || startTimeout.current) return;
        startTimeout.current = setTimeout(
          () => setEntered(true),
          (CARD_STAGGER * props.index + ENTRANCE_DURATION - COUNT_OVERLAP) *
            1000,
        );
      }}
      data-sanity={props.sanity}
      className={cn(
        'stat flex flex-col items-center justify-center gap-16 rounded-16 p-25 text-center l:p-40',
        props.cardClass,
      )}
      style={props.cardStyle}
    >
      <div
        className={cn(
          'font-planar font-[440] leading-95 -tracking-2',
          props.valueClass,
        )}
      >
        {props.animateValue ? (
          <AnimatedStatValue value={props.stat.value} start={entered} />
        ) : (
          props.stat.value
        )}
      </div>
      <div className="font-azeret text-12 font-normal uppercase leading-120 tracking-8">
        {props.stat.caption}
      </div>
    </motion.div>
  );
}

const GRID_COLS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 m:grid-cols-2',
  3: 'grid-cols-1 m:grid-cols-2 l:grid-cols-3',
  4: 'grid-cols-1 m:grid-cols-2 l:grid-cols-4',
} as const;

// Cap the grid width for low card counts so cards don't stretch oversized.
const GRID_MAX_WIDTH = {
  1: 'max-w-[400px]',
  2: 'max-w-[800px]',
  3: '',
  4: '',
} as const;

export function Stats(props: SanityStats & PageLayoutSharedProps) {
  const theme = props.theme ? THEME_STYLES[props.theme] : undefined;
  const count = Math.min(Math.max(props.stats.length, 1), 4) as 1 | 2 | 3 | 4;
  const valueClass =
    props.stats.length >= 4 ? 'text-50 l:text-65' : 'text-65 l:text-82';

  const cards = (
    <motion.div
      {...MOTION_PROPS}
      variants={gridVariants}
      className={cn(
        'mx-auto grid w-full gap-15 l:gap-25',
        GRID_COLS[count],
        GRID_MAX_WIDTH[count],
      )}
    >
      {props.stats.map((stat, index) => (
        <StatCard
          key={stat._key}
          stat={stat}
          index={index}
          // Module-level toggle, with a per-card override to skip count-up
          // (e.g. "#1" looks awkward animating 0 → 1 over the full duration).
          animateValue={
            Boolean(props.animateValue) && stat.disableAnimation !== true
          }
          valueClass={valueClass}
          cardClass={theme?.card}
          cardStyle={{
            // Fallback for legacy modules without a theme: use the colors
            // defined in Sanity for the card background and text.
            background: theme ? undefined : (props.statBackground ?? undefined),
            color: theme ? undefined : (props.statTextColor ?? undefined),
          }}
          sanity={createSanityFieldDataAttribute(
            props.sanitySource,
            `stats[_key=="${stat._key}"]`,
          )}
        />
      ))}
    </motion.div>
  );

  // Themed modules render as a full-bleed colored band.
  if (theme) {
    return (
      <div className={cn('p-main py-50 m:py-80 l:py-100', theme.section)}>
        <div className="mx-auto flex max-w-[1235px] flex-col items-center gap-32 text-center m:gap-48">
          {props.headline ? (
            <motion.h2
              {...MOTION_PROPS}
              variants={itemVariants}
              className="type-heading5 max-w-[800px] text-balance"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'headline',
              )}
            >
              {formatHeadline(props.headline, theme.accent)}
            </motion.h2>
          ) : null}
          {cards}
        </div>
      </div>
    );
  }

  // Fallback (legacy) modules keep the current transparent section background.
  return (
    <div className="p-main mx-auto max-w-[1235px] py-50 text-center m:py-80 l:py-100">
      {props.headline ? (
        <motion.h2
          {...MOTION_PROPS}
          variants={itemVariants}
          className="type-heading5 mx-auto mb-40 max-w-[800px] text-balance"
          data-sanity={createSanityFieldDataAttribute(
            props.sanitySource,
            'headline',
          )}
        >
          {formatHeadline(props.headline)}
        </motion.h2>
      ) : null}
      {cards}
    </div>
  );
}
